<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Icon from 'next/icon/Icon.vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  // Signed blob ids of the files that should be attached on save.
  initialFiles: { type: Array, default: () => [] },
});

const blobIds = defineModel({ type: Array, default: () => [] });
// Surfaced to the parent form: saving while a file is still on its way would store a canned
// response without it and leave the uploaded blob orphaned.
const isUploading = defineModel('uploading', { type: Boolean, default: false });

const { t } = useI18n();
const store = useStore();

const toEntry = file => ({
  signedId: file.signed_id,
  name: file.filename,
  previewUrl: file.file_url,
  isImage: String(file.file_type).startsWith('image/'),
});

const files = ref(props.initialFiles.map(toEntry));

const syncModel = () => {
  blobIds.value = files.value.map(file => file.signedId);
};

// The parent clears the model when it resets the form.
watch(blobIds, ids => {
  if (!ids.length) files.value = [];
});

const onFileChange = async event => {
  const selected = Array.from(event.target.files);
  event.target.value = '';
  if (!selected.length) return;

  isUploading.value = true;
  try {
    const uploads = await Promise.all(
      selected.map(async file => {
        const { blobId, fileUrl } = await store.dispatch(
          'uploadCannedResponseAttachment',
          file
        );
        return {
          signedId: blobId,
          name: file.name,
          previewUrl: fileUrl,
          isImage: String(file.type).startsWith('image/'),
        };
      })
    );
    files.value = [...files.value, ...uploads];
    syncModel();
  } catch {
    useAlert(t('CANNED_MGMT.ATTACHMENT.UPLOAD_ERROR'));
  } finally {
    isUploading.value = false;
  }
};

const removeFile = signedId => {
  files.value = files.value.filter(file => file.signedId !== signedId);
  syncModel();
};
</script>

<template>
  <div>
    <ul v-if="files.length" class="flex flex-col gap-2 p-0 mb-2 list-none">
      <li
        v-for="file in files"
        :key="file.signedId"
        class="flex items-center gap-2 px-2 py-2 text-xs border rounded-xl border-n-weak bg-n-alpha-2"
      >
        <div
          class="flex items-center justify-center overflow-hidden rounded-lg size-9 shrink-0 bg-n-solid-2 text-n-slate-11"
        >
          <img
            v-if="file.isImage"
            :src="file.previewUrl"
            :alt="file.name"
            class="object-cover size-full"
          />
          <Icon v-else icon="i-lucide-paperclip" />
        </div>
        <span class="flex-1 truncate text-n-slate-12">{{ file.name }}</span>
        <a
          v-tooltip.top="t('CANNED_MGMT.ATTACHMENT.PREVIEW_FILE')"
          :href="file.previewUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="flex items-center justify-center rounded-lg size-8 bg-n-solid-2 text-n-slate-11 hover:bg-n-alpha-3"
        >
          <Icon icon="i-lucide-eye" />
        </a>
        <button
          v-tooltip.top="t('CANNED_MGMT.ATTACHMENT.REMOVE_FILE')"
          type="button"
          class="flex items-center justify-center p-0 bg-transparent border-0 rounded-lg size-8 text-n-slate-10 hover:bg-n-ruby-3 hover:text-n-ruby-9"
          @click="removeFile(file.signedId)"
        >
          <Icon icon="i-lucide-x" />
        </button>
      </li>
    </ul>

    <label
      class="inline-flex items-center h-8 gap-1 px-2 py-1 text-xs border border-dashed rounded-lg cursor-pointer border-n-strong bg-n-background"
      :class="{ 'cursor-not-allowed opacity-70': isUploading }"
    >
      <input
        type="file"
        multiple
        class="hidden"
        :disabled="isUploading"
        @change="onFileChange"
      />
      <Spinner v-if="isUploading" />
      <Icon v-else icon="i-lucide-paperclip" />
      <span>{{ t('CANNED_MGMT.ATTACHMENT.ADD_FILE') }}</span>
    </label>
  </div>
</template>
